import { AlertTriangle, Camera, Check, Flashlight, FlashlightOff, Loader2, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";

/** Сколько держать «Готово» после отправки, мс. */
const DONE_MS = 300;

interface Props {
  /** Заголовок (напр. «Сфотографируйте Паллета 1»). */
  title: string;
  /** Снимок готов (JPEG blob) — вызывающий грузит его на бэк. */
  onCapture: (blob: Blob) => void;
  /** Закрыть без действия (крестик). Для обязательной фиксации = отмена
   *  создания паллеты. */
  onClose: () => void;
  /** Пропустить фото (только когда фотофиксация не обязательна). */
  onSkip?: () => void;
  /** Идёт загрузка снимка на бэк — блокируем кнопки. */
  busy?: boolean;
  /**
   * Процент отправки (0–100), если вызывающий его считает.
   *
   * ⚠️ Нужен именно на складском вайфае: без него «идёт загрузка» и «зависло»
   * выглядят одинаково, и кладовщик жмёт «Снять» ещё раз. Не передан —
   * показываем спиннер без числа, это честнее выдуманного прогресса.
   */
  progress?: number | null;
}

function humanError(e: unknown): string {
  const name = (e as { name?: string })?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError")
    return "Доступ к камере запрещён. Разрешите камеру в настройках браузера и обновите страницу.";
  if (name === "NotFoundError" || name === "OverconstrainedError") return "Камера не найдена.";
  if (name === "NotReadableError") return "Камера занята другим приложением.";
  return `Не удалось включить камеру: ${(e as Error)?.message ?? name ?? "ошибка"}`;
}

/** Съёмка фото камерой ТСД/телефона. Кадр берём из живого видео на canvas →
 *  JPEG. Прикрепление к документу 1С — снаружи.
 *
 *  ⚠️ Снимаем ВИДЕОКАДР, а не фотографию, и это потолок качества этого пути:
 *  video-трек отдаёт максимум того, что разрешит браузер, тогда как штатная
 *  камера телефона снимает через фото-API в полном разрешении матрицы.
 *  Замер на боевом портале 25.08.2026 (все 729 фото): было 900×1600 = 1,44 МП
 *  при 12 МП у телефона — то есть в восемь раз меньше пикселей, и на мелком
 *  тексте УПД и сертификатов это видно глазом. Жалобы склада были обоснованы.
 *
 *  Три источника потерь были такими: поток 1920×1080, ужатие до 1600 px и
 *  JPEG 0.8 — и они перемножались. Убраны все три (см. ниже). Следующий шаг,
 *  если и этого мало, — `ImageCapture.takePhoto()`: он снимает матрицей, а не
 *  видеопотоком, но есть не везде (в Safari на iOS нет), поэтому потребует
 *  запасного пути через этот же canvas. */
export function PhotoCapture({
  title,
  onCapture,
  onClose,
  onSkip,
  busy,
  progress,
}: Props): React.ReactElement {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [status, setStatus] = useState<"init" | "ready" | "error">("init");
  const [errMsg, setErrMsg] = useState("");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  /**
   * Снятый кадр, замороженный поверх видео.
   *
   * ⚠️ Без него момент съёмки НЕЗАМЕТЕН: видео продолжает шевелиться, кнопка
   * выглядит так же, и на складском вайфае человек не понимает, снялось ли
   * вообще. Отсюда боевая жалоба — кладовщики жмут «Снять» по многу раз и
   * получают пачку одинаковых снимков. Замороженный кадр отвечает на вопрос
   * «сфотографировалось?» сам по себе, ещё до того, как приедет ответ сервера.
   */
  const [shot, setShot] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw Object.assign(new Error("getUserMedia недоступен"), { name: "NotFoundError" });
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            // `ideal`, а не `exact`: устройство отдаёт лучшее, что умеет, и
            // камера включается даже там, где 4К нет. `exact` уронил бы съёмку
            // на слабом ТСД вместо того, чтобы снять как получится.
            width: { ideal: 3840 },
            height: { ideal: 2160 },
          },
          audio: false,
        });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const track = stream.getVideoTracks()[0];
        const caps = (track?.getCapabilities?.() ?? {}) as { torch?: boolean };
        setTorchSupported(Boolean(caps.torch));
        const v = videoRef.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play();
        setStatus("ready");
      } catch (e) {
        setStatus("error");
        setErrMsg(humanError(e));
      }
    })();
    return () => {
      stopped = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, []);

  async function toggleTorch() {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({
        advanced: [{ torch: !torchOn }],
      } as unknown as MediaTrackConstraints);
      setTorchOn((v) => !v);
    } catch {
      /* torch не поддержан — игнор */
    }
  }

  function snap() {
    const v = videoRef.current;
    if (!v || v.readyState < 2) return;
    // Пишем кадр КАК ЕСТЬ: ужатия больше нет. Оно стояло до 1600 px и срезало
    // ещё треть пикселей поверх и без того ограниченного видеопотока — а
    // снимают тут мелкий текст (УПД, сертификаты, этикетки), которому каждый
    // пиксель и нужен.
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
    // Замораживаем кадр СРАЗУ, не дожидаясь `toBlob` и тем более ответа
    // сервера: подтверждение съёмки должно быть мгновенным, иначе человек
    // снимает второй раз.
    //
    // ⚠️ Превью кодируем с УМЕНЬШЕННОГО холста. `toDataURL` синхронный, и на
    // полном кадре (3840×2160 ≈ 8 МП) он занимал главный поток ровно в тот
    // момент, который заморозка и должна делать мгновенным, — плюс держал
    // в состоянии base64-строку на сотни килобайт. Для картинки во весь
    // экран телефона 640 px по ширине достаточно.
    const preview = document.createElement("canvas");
    const scale = Math.min(1, 640 / (canvas.width || 1));
    preview.width = Math.round(canvas.width * scale);
    preview.height = Math.round(canvas.height * scale);
    preview.getContext("2d")?.drawImage(v, 0, 0, preview.width, preview.height);
    setShot(preview.toDataURL("image/jpeg", 0.6));
    // 0.92 вместо 0.8: на 0.8 JPEG заметно мылит именно мелкие буквы. Выше
    // 0.92 идти смысла нет — вес растёт быстро, а разница уже не видна.
    canvas.toBlob((blob) => blob && onCapture(blob), "image/jpeg", 0.92);
  }

  // Кадр держим, пока идёт отправка, и коротко показываем «Готово» после неё.
  //
  // ⚠️ Задержка ОДНА. Раньше она зависела от того, передан ли `busy` вообще
  // (900 мс против 300), то есть правило висело на ОТСУТСТВИИ пропа — а
  // отличить «не передан» от `false` на месте вызова нельзя. Все экраны его
  // передают, и ветка была недостижимой.
  useEffect(() => {
    if (!shot || busy) return;
    const t = setTimeout(() => setShot(null), DONE_MS);
    return () => clearTimeout(t);
  }, [shot, busy]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/95 p-3">
      <div className="mb-2 flex items-center justify-between text-sm text-white">
        <span className="font-medium">{title}</span>
        <div className="flex gap-2">
          {torchSupported && (
            <Button size="sm" variant="secondary" onClick={toggleTorch} disabled={busy}>
              {torchOn ? <FlashlightOff className="size-4" /> : <Flashlight className="size-4" />}
            </Button>
          )}
          <Button size="sm" variant="secondary" onClick={onClose} disabled={busy}>
            <X className="size-4" />
          </Button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden rounded-md bg-black">
        <video ref={videoRef} className="h-full w-full object-contain" playsInline muted autoPlay />
        {status === "init" && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-sm text-white">
            <Loader2 className="size-4 animate-spin" /> Включаем камеру…
          </div>
        )}
        {status === "error" && (
          <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-sm text-white">
            <div className="flex flex-col items-center gap-2">
              <AlertTriangle className="size-6 text-amber-400" />
              <span>{errMsg}</span>
            </div>
          </div>
        )}

        {/* Замороженный кадр поверх видео + КРУПНОЕ состояние отправки.
            Мелкий спиннер в кнопке эту задачу не решал: на складском вайфае
            его не замечают, и снимок делают заново. */}
        {shot && (
          <div className="absolute inset-0">
            <img src={shot} alt="" className="h-full w-full object-contain" />
            <div className="absolute inset-x-0 bottom-0 bg-black/70 p-4 text-center text-white">
              {busy ? (
                <>
                  <div className="flex items-center justify-center gap-2 text-base font-semibold">
                    <Loader2 className="size-5 animate-spin" />
                    Фото сделано, отправляем…
                  </div>
                  {typeof progress === "number" && (
                    <>
                      <div className="mx-auto mt-3 h-2 w-56 overflow-hidden rounded-full bg-white/25">
                        <div
                          className="h-full bg-white transition-[width] duration-200"
                          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                        />
                      </div>
                      <div className="mt-1 text-sm tabular-nums">
                        {Math.round(progress)}%
                      </div>
                    </>
                  )}
                  <div className="mt-2 text-xs text-white/70">
                    Не снимайте ещё раз — снимок уже в очереди.
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center gap-2 text-base font-semibold">
                  <Check className="size-5" />
                  Готово
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-center gap-2">
        {onSkip && (
          <Button variant="outline" className="flex-1" onClick={onSkip} disabled={busy}>
            Пропустить
          </Button>
        )}
        <Button
          className="flex-[2]"
          onClick={snap}
          disabled={status !== "ready" || busy || Boolean(shot)}
        >
          {busy ? (
            <>
              <Loader2 className="mr-1 size-4 animate-spin" />
              Отправляем…
            </>
          ) : (
            <>
              <Camera className="mr-1 size-4" />
              Снять и продолжить
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
